/**
 * Submit-time helpers used by OrderStart / OrderReadyToSend.
 *
 * After the ordering-domain → microapp consolidation, the SPA reads the test
 * catalog and per-test consent flow directly from ``UiConfig.testTypes``.
 * The HTTP route ``/config/ordering-domain/{org}`` no longer exists.
 */

import { getTestTypeConfig, type UiConfig } from '@/config/uiConfig'
import { ConsentFlow } from '@/ordering/consentFlow'
import { TestType } from '@/types/RequisitionFormTypes'

export type DomainTestLine = { code: string; consentFlowId: string }

/**
 * Build the submit catalog (code → consentFlowId) from the microapp UI config.
 * ``consentFlow`` defaults to ``'DEFAULT'`` so downstream resolution can fall
 * back to the org-level ``UiConfig.consentFlow``.
 */
export function testLinesFromUiConfig(config: Pick<UiConfig, 'testTypes'> | null | undefined): DomainTestLine[] {
  const types = Array.isArray(config?.testTypes) ? config!.testTypes : []
  return types.flatMap((t) => {
    const code = typeof t.code === 'string' ? t.code.trim() : ''
    if (!code) return []
    const consentFlowId = typeof t.consentFlow === 'string' && t.consentFlow.length > 0 ? t.consentFlow : 'DEFAULT'
    return [{ code, consentFlowId }]
  })
}

export function consentFlowIdForSubmit(selectedTestCode: string | undefined | null, catalog: DomainTestLine[]): string {
  const code = selectedTestCode?.trim() ?? ''
  if (!code) return 'DEFAULT'
  const row = catalog.find((t) => t.code === code)
  return row?.consentFlowId?.trim() || 'DEFAULT'
}

const CONSENT_METHOD_AB = 'CONSENT_METHOD_AB'

/** Per-test flows that a Mode AB health system upgrades to `PRACTITIONER_MEDIATED_AB`. */
const MODE_AB_UPGRADABLE_FLOWS: ReadonlySet<string> = new Set([
  '',
  'DEFAULT',
  ConsentFlow.PractitionerMediated,
  'PRACTIONER_MEDIATED',
])

/**
 * Whether the health system is configured as Mode AB, either through the Config Console
 * consent method or through the org-level consent flow.
 */
function isModeAbHealthSystem(config: Pick<UiConfig, 'consentFlow' | 'consentMethod'>): boolean {
  return (
    (config.consentMethod ?? '').trim().toUpperCase() === CONSENT_METHOD_AB ||
    (config.consentFlow ?? '').trim().toUpperCase() === ConsentFlow.PractitionerMediatedAb
  )
}

/**
 * Resolves the effective ordering consent flow (UPPER_CASE) for the selected test,
 * matching OrderReadyToSend: per-test catalog row, org default, per-test UI config, then catalog fallback.
 *
 * Test lines keep `PRACTITIONER_MEDIATED` in a Mode AB health system, so without the upgrade
 * a per-test value would mask Mode AB and comparators added after submission would never
 * require a deliverable contact.
 */
export function resolveEffectiveConsentFlowForSubmit(
  config: Pick<UiConfig, 'consentFlow' | 'consentMethod' | 'testTypes'>,
  selectedTestCode: string | undefined | null,
  catalog: DomainTestLine[]
): string {
  const testCode = selectedTestCode?.trim() ?? ''
  const catalogRow = catalog.find((t) => t.code === testCode)
  const lineConsent = catalogRow?.consentFlowId?.trim()
  const orderingDomainDefaultConsentFlow = (config.consentFlow ?? '').trim() || null
  const uiTestConsentFlow = getTestTypeConfig(testCode as TestType, config as UiConfig)?.consentFlow?.trim()

  const resolved =
    lineConsent && lineConsent !== 'DEFAULT'
      ? lineConsent
      : (orderingDomainDefaultConsentFlow || '').trim() ||
        (uiTestConsentFlow && uiTestConsentFlow !== 'DEFAULT' ? uiTestConsentFlow : '') ||
        (config.consentFlow || '').trim() ||
        consentFlowIdForSubmit(testCode, catalog)

  if (isModeAbHealthSystem(config) && MODE_AB_UPGRADABLE_FLOWS.has(resolved.toUpperCase())) {
    return ConsentFlow.PractitionerMediatedAb
  }
  return resolved
}
